import { ContactInfo } from '@weave/schema-gen-ts/dist/schemas/persons/v3/contact_info.pb';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { PersonsV3 } from '@frontend/api-person';
import { i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';

dayjs.extend(advancedFormat);

type DestinationLabel = {
  destination?: string;
  label?: string;
};

/**
 * This function is used to combine the labels of the destinations into a single string.
 * @param destinations - array of destination labels
 * @returns {string} - a string with the combined labels
 */

export const combineDestinationLabels = (destinations: DestinationLabel[]) =>
  destinations.map((destination) => destination.label).join('/');

/**
 * This function is the final formatted destination label. If the label is not provided, it will be empty.
 * @param obj - the destination and label to format
 * @returns {string} - a formatted string with the destination label and the phone number.
 */

export const formatDestinationLabel = ({ destination, label }: DestinationLabel) =>
  `${label ?? ''} ${formatPhoneNumber(destination)}`.trim();

/**
 * This function is used to get the matching destination from the array of destinations.
 * @param destinations - array of destination labels
 * @param match - the destination to match against
 * @returns {DestinationLabel[]} - an array of matching destinations
 */

export const getMatchingDestination = (destinations: DestinationLabel[] = [], match?: string) =>
  destinations.filter(({ destination }) => destination && match?.includes(destination));

/**
 * This function is used to format the destination label for the auto message suppression card.
 * @param contactInfo - contact info data should come from the SchemaPersonV3Service.GetPersonLegacy endpoint
 * @param originalDestination - the destination to match against
 * @returns {string} - a formatted string with the destination(s) label and the phone number.
 */

export const formatMatchingDestinations = (contactInfo: ContactInfo[] = [], originalDestination?: string) => {
  const destinations = contactInfo?.map(({ destination, type }) => ({
    destination,
    label: PersonsV3.PersonHelpers.contactTypeLabels[type],
  }));
  const matchingDestinations = getMatchingDestination(destinations, originalDestination);

  // no matches, so we will not specify the destination type
  if (!matchingDestinations.length) return formatDestinationLabel({ destination: originalDestination, label: '' });

  // combine the labels of the matching destinations
  const label = combineDestinationLabels(matchingDestinations);
  return formatDestinationLabel({ destination: originalDestination, label });
};

export const TIME_FORMAT = 'h:mm A';
export const DATE_FORMAT = 'MMMM Do';
export const DAY_FORMAT = 'dddd';

export const formatFutureTimestamp = ({
  simplified,
  time,
  willNotSend,
}: {
  simplified?: boolean;
  time?: string;
  willNotSend: boolean;
}) => {
  const now = dayjs();
  const timestamp = dayjs(time);
  const dateValue = timestamp.format(DATE_FORMAT); // e.g. March 3rd
  const dayValue = timestamp.format(DAY_FORMAT); // e.g. Monday
  const timeValue = timestamp.format(TIME_FORMAT); // e.g. 3:30 PM

  const withinHour = now.add(1, 'hour').isAfter(dayjs(time));
  const withinDay = now.startOf('day').isBefore(timestamp) && timestamp.isBefore(now.endOf('day'));
  const withinTomorrow =
    now.add(1, 'day').startOf('day').isBefore(timestamp) && timestamp.isBefore(now.add(1, 'day').endOf('day'));
  const withinWeek = now.add(1, 'week').isAfter(timestamp);

  const simpifiedTodayTimestamp = i18next.t('Today at {{time}}', { ns: 'messages', time: timeValue });
  const simpifiedTomorrowTimestamp = i18next.t('Tomorrow at {{time}}', { ns: 'messages', time: timeValue });

  if (withinHour) {
    const minuteDifference = timestamp.diff(now, 'minute');
    // is exactly 60 minutes
    if (minuteDifference === 60)
      return willNotSend
        ? i18next.t('Not sending in 1 hour', { ns: 'messages' })
        : simplified
        ? simpifiedTodayTimestamp
        : i18next.t('Preparing to send in 1 hour', { ns: 'messages' });
    return willNotSend
      ? i18next.t('Not sending in {{count}} minute', { ns: 'messages', count: minuteDifference })
      : simplified
      ? simpifiedTodayTimestamp
      : i18next.t('Preparing to send in {{count}} minute', { ns: 'messages', count: minuteDifference });
  } else if (withinDay) {
    return willNotSend
      ? i18next.t('Not sending today at {{time}}', { ns: 'messages', time: timeValue })
      : simplified
      ? simpifiedTodayTimestamp
      : i18next.t('Sending today at {{time}}', { ns: 'messages', time: timeValue });
  } else if (withinTomorrow) {
    return willNotSend
      ? i18next.t('Not sending tomorrow at {{time}}', { ns: 'messages', time: timeValue })
      : simplified
      ? simpifiedTomorrowTimestamp
      : i18next.t('Sending tomorrow at {{time}}', { ns: 'messages', time: timeValue });
  } else if (withinWeek) {
    return willNotSend
      ? i18next.t('Not sending {{day}} at {{time}}', { ns: 'messages', day: dayValue, time: timeValue })
      : simplified
      ? i18next.t('{{day}} at {{time}}', { ns: 'messages', day: dayValue, time: timeValue })
      : i18next.t('Sending {{day}} at {{time}}', { ns: 'messages', day: dayValue, time: timeValue });
  } else {
    return willNotSend
      ? i18next.t('Not sending {{date}} at {{time}}', {
          ns: 'messages',
          date: dateValue,
          time: timeValue,
        })
      : simplified
      ? i18next.t('{{date}} at {{time}}', {
          ns: 'messages',
          date: dateValue,
          time: timeValue,
        })
      : i18next.t('Sending {{date}} at {{time}}', {
          ns: 'messages',
          date: dateValue,
          time: timeValue,
        });
  }
};

export const formatPastTimestamp = ({ hasSent, time }: { hasSent: boolean; time?: string }) => {
  const now = dayjs();
  const dateValue = dayjs(time).format(DATE_FORMAT); // e.g. March 3rd
  const dayValue = dayjs(time).format(DAY_FORMAT); // e.g. Monday
  const timeValue = dayjs(time).format(TIME_FORMAT); // e.g. 3:30 PM

  const withinToday = now.startOf('day').isBefore(dayjs(time));
  const withinYesterday = now.subtract(1, 'day').startOf('day').isBefore(dayjs(time));
  const withinWeek = now.subtract(1, 'week').isBefore(dayjs(time));

  if (withinToday) {
    return hasSent
      ? i18next.t('Sent today at {{time}}', { ns: 'messages', time: timeValue })
      : i18next.t('Not sent today at {{time}}', { ns: 'messages', time: timeValue });
  } else if (withinYesterday) {
    return hasSent
      ? i18next.t('Sent yesterday at {{time}}', { ns: 'messages', time: timeValue })
      : i18next.t('Not sent yesterday at {{time}}', { ns: 'messages', time: timeValue });
  } else if (withinWeek) {
    return hasSent
      ? i18next.t('Sent {{day}} at {{time}}', { ns: 'messages', day: dayValue, time: timeValue })
      : i18next.t('Not sent {{day}} at {{time}}', { ns: 'messages', day: dayValue, time: timeValue });
  } else {
    return hasSent
      ? i18next.t('Sent {{date}} at {{time}}', { ns: 'messages', date: dateValue, time: timeValue })
      : i18next.t('Not sent {{date}} at {{time}}', { ns: 'messages', date: dateValue, time: timeValue });
  }
};
